import { Integrations } from '@static/js/types';
import { ConsentCategory, ConsentState } from './polarisConsent';
import { consentLogger } from './consentLogger';

/**
 * Segment integration configuration value
 * - boolean: Enable (true) or disable (false) the integration
 * - object: Enable with integration-specific options
 */
export type SegmentIntegrationValue = boolean | Record<string, unknown>;

/**
 * Segment integrations configuration object
 */
export type SegmentIntegrationsConfig = Readonly<Partial<Record<Integrations, SegmentIntegrationValue>>>;

/**
 * Integration configuration structure
 */
export interface IntegrationConfig {
  /** Whether this integration is enabled (false = always disabled regardless of consent) */
  enabled: boolean;
  /** Consent categories required for this integration (undefined = no consent required) */
  consent?: ReadonlyArray<ConsentCategory>;
  /** Integration-specific options (e.g., apiHost for SegmentIO) */
  options?: Record<string, unknown>;
}

/**
 * Configuration mapping type for Segment integrations
 */
type IntegrationConsentConfig = {
  [K in Integrations]?: IntegrationConfig;
};

/**
 * Configuration mapping Segment integrations to their requirements
 */
export const INTEGRATION_CONSENT_CONFIG: IntegrationConsentConfig = {
  // Core Segment with custom API host - always enabled
  [Integrations.SegmentIO]: {
    enabled: true,
    options: {
      apiHost: 'p.withjoy.com/segapi/v1',
    },
  },

  // Always disabled integrations
  [Integrations.Intercom]: {
    enabled: false,
  },
  [Integrations.Chameleon]: {
    enabled: false,
  },

  // Statistics consent required
  [Integrations.GoogleAnalytics4Web]: {
    enabled: true,
    consent: ['statistics'],
  },
  [Integrations.Amplitude]: {
    enabled: true,
    consent: ['statistics'],
  },

  // Marketing consent required
  [Integrations.FacebookPixel]: {
    enabled: true,
    consent: ['marketing'],
  },
  [Integrations.BingAds]: {
    enabled: true,
    consent: ['marketing'],
  },
  [Integrations.Criteo]: {
    enabled: true,
    consent: ['marketing'],
  },
} as const;

/**
 * Build Segment integrations config based on consent state
 *
 * This function iterates through all configured integrations and determines
 * which should be enabled based on the user's consent preferences.
 *
 * @param consent - The current user consent state for all categories
 * @returns Segment integrations configuration object with enabled/disabled integrations
 *
 * @example
 * ```typescript
 * const config = buildIntegrationsConfig({
 *   essential: true,
 *   statistics: true,
 *   marketing: false
 * });
 * // Returns: { 'Segment.io': { apiHost: '...' }, 'Google Analytics 4 Web': true, ... }
 * ```
 */
export function buildIntegrationsConfig(consent: ConsentState): SegmentIntegrationsConfig {
  const integrations: Partial<Record<Integrations, SegmentIntegrationValue>> = {};

  // Use typed keys for better type safety
  const integrationKeys = Object.keys(INTEGRATION_CONSENT_CONFIG) as Integrations[];

  for (const integration of integrationKeys) {
    const config = INTEGRATION_CONSENT_CONFIG[integration];
    if (!config) continue;

    try {
      // Always disabled integrations
      if (!config.enabled) {
        integrations[integration] = false;
        continue;
      }

      // Check if consent requirements are met
      if (config.consent && config.consent.length > 0) {
        const hasConsent = config.consent.every((category) => consent[category]);

        if (!hasConsent) {
          integrations[integration] = false;
          continue;
        }
      }

      // Integration is enabled - return options if provided, otherwise true
      integrations[integration] = config.options || true;
    } catch (error) {
      consentLogger.error(`Error processing integration ${integration}:`, error);
      // Fail safe: disable integration on error
      integrations[integration] = false;
    }
  }

  return integrations;
}
